/**
 * TurnAttachments — the files a user actually sent, shown on their message.
 *
 * A document uploaded to a research chat used to vanish from the UI the
 * moment it was sent: it was ingested, and it came back later as an
 * "Uploaded document" citation, but nothing said "you attached this". These
 * chips are that acknowledgement.
 *
 * Not `AttachmentPills` — those belong to the composer and carry upload
 * status and a remove button. A sent file has neither; it is a fact.
 *
 * A chip becomes a button when the file has a storage path — its own, or one
 * borrowed from the citation the engine made of it. An upload is a stored
 * blob like any other and `stream-source` decrypts it, so opening a file does
 * NOT depend on the engine having cited it. Only a file with no path at all
 * stays a plain chip, rather than offering an action that would fail.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiPaperclipLight } from 'react-icons/pi';
import { formatBytes } from '../../../utils/functions';
import { TurnAttachment, attachmentCitation } from '../attachments';

interface Props {
  attachments?: TurnAttachment[];
  /** Citations from the answer to this message — where an openable copy is. */
  citations?: any[];
  onOpen?: (citation: any) => void;
}

const TurnAttachments: React.FC<Props> = ({ attachments, citations, onOpen }) => {
  const { t } = useTranslation();
  if (!attachments || attachments.length === 0) return null;

  return (
    <div
      className="mt-1.5 flex flex-wrap justify-end gap-1.5"
      data-testid="turn-attachments"
    >
      {attachments.map((attachment, i) => {
        const citation = onOpen ? attachmentCitation(attachment, citations) : null;
        // Size is optional upstream; 0 is not a size worth printing.
        const size = attachment.size ? formatBytes(attachment.size) : '';

        const body = (
          <>
            <PiPaperclipLight className="w-3.5 h-3.5 text-fg-muted flex-shrink-0" aria-hidden />
            <span className="max-w-[180px] truncate">{attachment.name}</span>
            {size && <span className="text-[10px] text-fg-muted flex-shrink-0">{size}</span>}
          </>
        );

        const className = clsx(
          'inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs',
          'bg-surface-sunken border border-hairline-soft text-fg-body',
          'transition-colors duration-200 ease-slide',
          citation && 'hover:text-champagne hover:border-champagne/40'
        );

        return citation ? (
          <button
            key={attachment.uuid || `${attachment.name}-${i}`}
            type="button"
            onClick={() => onOpen?.(citation)}
            data-testid="turn-attachment"
            data-openable="true"
            title={`${t('Open')} ${attachment.name}`}
            className={className}
          >
            {body}
          </button>
        ) : (
          <span
            key={attachment.uuid || `${attachment.name}-${i}`}
            data-testid="turn-attachment"
            title={attachment.name}
            className={className}
          >
            {body}
          </span>
        );
      })}
    </div>
  );
};

export default TurnAttachments;
